import {useState} from 'react';
import {Box, Flex, Image, SimpleGrid, Text, Tooltip} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {useHalState} from '@synako/haligator';
import {Pie, PieChart} from 'recharts';
import {
  CodeLabelEGalim,
  DetailLabelEGalim,
  SyntheseEGalim,
} from 'api-types/ffconnect';
import {toDecimalFr} from 'ff-common';
import {useLabelAsset} from '@/haligator-factory';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';
import colors from '@/chakra/colors';
import {PoidsIcon} from '@/components/svg/PoidsIcon';
import {HStack} from '@chakra-ui/layout';
import {LABELS_ELIGIBLES, LABELS_OFFICIELS} from '../egalim-constantes';

/**
 * Donut Recharts affichant un pourcentage.
 */
const EgalimDonut: React.FC<{pourcentage: number; size?: number}> = ({
  pourcentage,
  size = 100,
}) => {
  // Donut via 2 PieChart superposés : un cercle de fond (bgData) et un arc de progression (fgData)
  // Le pourcentage est borné entre 0 et 100 pour éviter les débordements visuels
  const clamped = Math.min(Math.max(pourcentage, 0), 100);
  const bgData = [{value: 100, fill: colors.blue.sky_light}];
  const fgData = [
    {value: clamped, fill: colors.blue.main},
    {value: 100 - clamped, fill: 'transparent'},
  ];

  return (
    <Box
      position='relative'
      w={`${size}px`}
      h={`${size}px`}
      mx='auto'
      my={2}
    >
      <PieChart
        width={size}
        height={size}
      >
        {/* Fond : cercle complet sans arrondi */}
        <Pie
          data={bgData}
          cx='50%'
          cy='50%'
          innerRadius={size / 2 - 10}
          outerRadius={size / 2 - 3}
          dataKey='value'
          isAnimationActive={false}
          stroke='none'
        />
        {/* Segment actif avec arrondi */}
        {clamped > 0 && (
          <Pie
            data={fgData}
            cx='50%'
            cy='50%'
            innerRadius={size / 2 - 10}
            outerRadius={size / 2 - 3}
            startAngle={90}
            endAngle={-270}
            dataKey='value'
            cornerRadius={4}
            isAnimationActive={false}
            stroke='none'
          />
        )}
      </PieChart>
      <Flex
        position='absolute'
        inset={0}
        align='center'
        justify='center'
      >
        <Text
          fontSize='md'
          fontWeight='bold'
          color='gray.dark'
        >
          {toDecimalFr(pourcentage, 0, 0)}%
        </Text>
      </Flex>
    </Box>
  );
};

/** Image de label chargée via HAL, avec fallback texte (même pattern que DisplayLabels) */
const LabelImage: React.FC<{code: string; libelle: string}> = ({
  code,
  libelle,
}) => {
  const labelHook = useHalState(useLabelAsset(code));
  const [hasError, setHasError] = useState(false);
  const labelImage = imageOptimisee(labelHook.data?.url, 36);

  if (hasError || labelHook.error) {
    return null;
  }

  return (
    <Tooltip label={libelle}>
      <Image
        boxSize='36px'
        src={labelImage}
        alt={libelle}
        objectFit='contain'
        flexShrink={0}
        onError={() => {
          setHasError(true);
        }}
      />
    </Tooltip>
  );
};

/** Carte individuelle pour un label */
const LabelCard: React.FC<{
  code: CodeLabelEGalim;
  libelle: string;
  pourcentage: number;
  poids: number;
  montant: number;
}> = ({code, libelle, pourcentage, poids, montant}) => {
  const {t} = useTranslation();
  return (
    <Box
      bg='white'
      border='1px solid'
      borderColor='gray.200'
      borderRadius='8px'
      p={3}
    >
      {/* Header : logo + nom */}
      <Flex
        align='center'
        gap={2}
        minH='40px'
      >
        <LabelImage
          code={code}
          libelle={libelle}
        />
        <Text
          fontSize='sm'
          fontWeight='600'
          color='black.text'
          noOfLines={2}
        >
          {libelle}
        </Text>
      </Flex>

      {/* Donut */}
      <EgalimDonut pourcentage={pourcentage} />

      {/* Footer : poids + montant */}
      <Flex
        justify='space-between'
        align='center'
        mt={1}
      >
        <HStack alignItems='center'>
          <PoidsIcon
            width='12px'
            height='12px'
            color='gray.dark'
          />
          <Text
            fontSize='xs'
            color='gray.dark'
          >
            {t('egalim.poids_kg', {poids: toDecimalFr(poids, 2, 2)})}
          </Text>
        </HStack>

        <Text
          fontSize='sm'
          fontWeight='bold'
          color='blue.main'
        >
          {t('egalim.montant_euro', {montant: toDecimalFr(montant, 0, 0)})}
        </Text>
      </Flex>
    </Box>
  );
};

/**
 * Grille de détails par label ÉGalim avec donuts circulaires.
 * Affiche tous les labels (même ceux à 0%).
 */
export const EgalimDetailLabels: React.FC<{
  synthese: SyntheseEGalim;
  labelsSelectionnes: CodeLabelEGalim[];
}> = ({synthese, labelsSelectionnes}) => {
  const {t} = useTranslation();

  // Map code_label → detail pour lookup rapide
  const detailsMap = new Map<string, DetailLabelEGalim>();
  for (const detail of synthese.details_par_label ?? []) {
    if (detail.label?.code) {
      detailsMap.set(detail.label.code, detail);
    }
  }

  // Labels officiels + labels éligibles sélectionnés par le client
  const labelsAffiches = [
    ...LABELS_OFFICIELS,
    ...LABELS_ELIGIBLES.filter((l) => labelsSelectionnes.includes(l.code)),
  ];

  return (
    <Box mt={6}>
      <Text
        fontWeight='600'
        color='gray.dark'
        fontSize='md'
        mb={4}
      >
        {t('egalim.details_par_label')}
      </Text>
      <SimpleGrid
        columns={{base: 1, sm: 2, md: 3, lg: 5}}
        spacing={4}
      >
        {labelsAffiches.map(({code, libelle}) => {
          const detail = detailsMap.get(code);
          return (
            <LabelCard
              key={code}
              code={code}
              libelle={libelle}
              pourcentage={detail?.pourcentage_montant ?? 0}
              poids={detail?.poids ?? 0}
              montant={detail?.montant ?? 0}
            />
          );
        })}
      </SimpleGrid>
    </Box>
  );
};
